"use client";

import React, { useState, useMemo } from "react";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function BooksConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [selectedTopic, setSelectedTopic] = useState("All Books");
  const topics = useMemo(() => {
    const uniqueTopics = new Set<string>();
    posts.forEach(post => {
      if (post.customFields?.topic) {
        uniqueTopics.add(post.customFields.topic);
      }
    });
    return ["All Books", ...Array.from(uniqueTopics)];
  }, [posts]);
  const filteredBooks = useMemo(() => {
    if (selectedTopic === "All Books") return posts;
    return posts.filter(post => post.customFields?.topic === selectedTopic);
  }, [posts, selectedTopic]);
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} className="h-[420px] animate-pulse rounded-lg bg-muted/50" />)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center">
        <p className="text-muted-foreground font-default">Failed to load the library. Please try again later.</p>
      </div>;
  }
  return <div className="space-y-12">
      {}
      <div className="flex flex-wrap justify-center gap-3">
        {topics.map((topic, i) => <button key={topic} data-index={i} onClick={() => setSelectedTopic(topic)} className={`rounded-md px-6 py-2 text-sm font-bold tracking-wider uppercase transition-all duration-300 font-default border ${selectedTopic === topic ? "bg-accent border-accent text-primary" : "bg-transparent border-border text-muted-foreground hover:border-accent hover:text-accent"}`}>
            {topic}
          </button>)}
      </div>

      {}
      {filteredBooks.length > 0 ? <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredBooks.map((post, i) => <div key={post.id} data-index={i} className="h-full">
              <Card className="group relative flex h-[420px] w-full flex-col overflow-hidden rounded-lg border-0 bg-transparent p-0 shadow-md transition-shadow duration-300 hover:shadow-xl">
                {}
                {post.customFields?.book_cover_image && <img src={post.customFields.book_cover_image} data-wvc-srcset={`${post.customFields.book_cover_image}-wvc-srcset`} data-wvc-sizes={`${post.customFields.book_cover_image}-wvc-sizes`} alt={post.customFields?.title || "Book Cover"} loading="lazy" className="absolute inset-0 z-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                
                {}
                <div className="absolute inset-0 z-10 bg-primary/60 transition-colors duration-300 group-hover:bg-primary/80" />

                {}
                <div className="relative z-20 flex h-full flex-col justify-between p-8">
                  <CardHeader className="p-0">
                    <div className="mb-4">
                      {post.customFields?.topic && <span className="inline-block rounded-md bg-accent/20 px-3 py-1 text-[10px] font-bold tracking-widest text-accent uppercase font-default border border-accent/30">
                          {post.customFields.topic}
                        </span>}
                    </div>
                    <Link to={post.link || "#"} className="block">
                      <CardTitle className="font-serif text-2xl font-bold leading-tight text-primary-foreground md:text-3xl hover:text-accent transition-colors">
                        {post.customFields?.title}
                      </CardTitle>
                    </Link>
                    <CardDescription className="mt-4 text-sm leading-relaxed text-primary-foreground/90 opacity-0 transition-all duration-300 group-hover:opacity-100 font-default line-clamp-4">
                      {post.customFields?.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardFooter className="mt-auto flex items-center justify-between p-0">
                    <Link to={post.customFields?.amazon_link || "#"} newTab className="inline-flex items-center gap-2 rounded-md bg-accent px-6 py-3 text-sm font-bold text-primary shadow-sm transition-all duration-300 hover:bg-accent/90 hover:-translate-y-1 font-default">
                      Buy on Amazon
                      <ExternalLink className="size-4" />
                    </Link>
                    
                    <Link to={`/SingleBookTemplate?id=${post.id}`} className="inline-flex size-12 items-center justify-center rounded-md bg-white/10 text-primary-foreground backdrop-blur-sm transition-transform duration-300 hover:bg-white/20">
                      <ArrowUpRight className="size-6" />
                    </Link>
                  </CardFooter>
                </div>
              </Card>
            </div>)}
        </div> : <div className="py-20 text-center">
          <p className="text-xl text-muted-foreground font-default italic">No books found for this topic.</p>
        </div>}
    </div>;
}
export default function ServicesGrid() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const wp_query_params = {
    post_type: "book",
    per_page: 100,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="254626" id="full-catalog" className="bg-background text-foreground py-20 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerReveal.ref} className={`mb-16 text-center transition-all duration-700 ${headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <span className="mb-4 inline-block text-sm font-bold tracking-widest text-accent uppercase font-default">
            The Library
          </span>
          <h2 className="font-serif text-4xl font-bold tracking-tight text-primary md:text-5xl lg:text-6xl">
            Explore the full collection
            <br className="hidden md:block" /> of faith-based resources.
          </h2>
        </div>

        <div ref={gridReveal.ref} className={`transition-all duration-1000 delay-200 ${gridReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <PostsProvider wp_query={{
          post_type: "book",
          per_page: 100,
          orderby: "date",
          order: "desc"
        }}>
            <BooksConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>;
}